import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { CSVLink } from 'react-csv';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  cdqDetailTableHeaders,
  CollateralDataQualitySortMode,
  collateralDataQualitySortModeOptions,
  CollateralDataQualityViewMode,
  getReportingDatesString,
} from './utils';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import { IsolatedMultiSelect } from '~/packages/common-frontend/components/MultiSelect';
import { SearchBar } from '~/packages/common-frontend/components/SearchAndCopyComponents';
import { Select } from '~/packages/common-frontend/components/Select';

const resultBadgeStyles = {
  pass: {
    fontColor: colors.success,
    backgroundColor: colors.green[100],
    icon: 'circle-check',
  },
  fail: {
    fontColor: colors.error,
    backgroundColor: colors.red[100],
    icon: 'circle-x',
  },
};

export const CollateralDataQualityCheckResultBadge = ({
  numberOfFailures,
  badgeSize,
}: {
  numberOfFailures: number;
  badgeSize: 'large' | 'small';
}) => {
  const isPassed = numberOfFailures === 0;
  const badgeStyles = resultBadgeStyles[isPassed ? 'pass' : 'fail'];

  return (
    <Flex
      borderRadius="8px"
      backgroundColor={badgeStyles.backgroundColor}
      p={badgeSize === 'small' ? '4px 8px' : '8px 16px'}
      w="fit-content"
      alignItems="center"
    >
      <Icon icon={badgeStyles.icon} color={badgeStyles.fontColor} />
      <Text color={badgeStyles.fontColor} ml="8px" fontSize="14px">
        {isPassed ? 'Passed' : `${numberOfFailures} Failed`}
      </Text>
    </Flex>
  );
};

export const CollateralDataQualityCheckReportingDatesBadge = ({
  prevReportingDates,
  currentReportingDates,
}: {
  prevReportingDates: Util.FullDateStr[];
  currentReportingDates: Util.FullDateStr[];
}) => {
  const reportingDatesString = useMemo(() => {
    const ascPrevReportingDates = prevReportingDates.sort();
    const ascCurrentReportingDates = currentReportingDates.sort();
    const firstPrevReportingDate = _.first(ascPrevReportingDates);
    const lastCurrentReportingDate = _.last(ascCurrentReportingDates);

    return getReportingDatesString({
      firstReportingDate: firstPrevReportingDate,
      lastReportingDate: lastCurrentReportingDate,
    });
  }, [currentReportingDates, prevReportingDates]);

  return (
    <Flex
      borderWidth="1px"
      borderRadius="8px"
      borderColor={colors.gray[200]}
      p="4px 16px"
      w="fit-content"
      alignItems="center"
    >
      <Icon icon="calendar" color={colors.black[70]} />
      <Text color={colors.black[70]} ml="8px" fontSize="14px">
        {reportingDatesString}
      </Text>
    </Flex>
  );
};

export const LastCheckedBadge = ({ date }: { date: Date }) => {
  const lastCheckedDateStr = useMemo(
    () => commonDate.epochToLocaleDateString(commonDate.dateToEpoch(date)),
    [date],
  );
  return (
    <Text
      color={colors.black[70]}
      fontSize="14px"
    >{`Last Checked: ${lastCheckedDateStr}`}</Text>
  );
};

export const CollateralDataQualityCheckBreadcrumbs = ({
  setViewMode,
  cdqConfigName,
}: {
  setViewMode: React.Dispatch<
    React.SetStateAction<CollateralDataQualityViewMode>
  >;
  cdqConfigName: string;
}) => {
  return (
    <Flex alignItems="center">
      <Text
        fontSize="12px"
        color={colors.black[70]}
        mr="8px"
        cursor="pointer"
        _hover={{ textDecoration: 'underline' }}
        onClick={() => {
          setViewMode('overview');
        }}
      >
        Collateral Data Quality
      </Text>
      <Icon icon="chevron-right" color={colors.black[70]} boxSize={3} />
      <Text fontSize="12px" color={colors.black[70]} ml="8px" cursor="default">
        {cdqConfigName}
      </Text>
    </Flex>
  );
};

export const CollateralDataQualityCheckResultActionsMenu = ({
  onClickArchiveConfig,
  athenaQueryDataItems,
  cdqConfigName,
}: {
  onClickArchiveConfig: () => Promise<void>;
  athenaQueryDataItems: any[] | undefined;
  cdqConfigName: string;
}) => {
  const fileDownloadHeaders = useMemo(() => {
    return cdqDetailTableHeaders.map((header) => ({
      label: commonUtils.toTitleCase(header),
      key: header,
    }));
  }, []);

  const currentDateTime = commonDate.currentDateTime();
  const filename = `${_.snakeCase(cdqConfigName)}-${currentDateTime}.csv`;

  return (
    <Menu>
      <MenuButton as={Button} variant="ghost" px="4px" mb="2px">
        <Icon icon="ellipsis-vertical" />
      </MenuButton>
      <MenuList>
        <MenuItem
          icon={<Icon icon="box" />}
          onClick={onClickArchiveConfig}
          color={colors.black[70]}
        >
          Archive
        </MenuItem>
        {!!athenaQueryDataItems?.length && (
          <CSVLink
            data={athenaQueryDataItems}
            headers={fileDownloadHeaders}
            filename={filename}
          >
            <MenuItem
              icon={<Icon icon="arrow-down-to-bracket" />}
              color={colors.black[70]}
            >
              <Text>Download CSV</Text>
            </MenuItem>
          </CSVLink>
        )}
      </MenuList>
    </Menu>
  );
};

export const CollateralDataQualityCheckNoConfigsDisplay = ({
  doConfigsExist,
}: {
  doConfigsExist: boolean;
}) => {
  if (doConfigsExist) {
    return (
      <Flex alignItems="center" mt="24px">
        <Text>
          No collateral data quality checks match the applied filters.
        </Text>
      </Flex>
    );
  }

  return <Text>None configured.</Text>;
};

type CollateralDataQualityCheckOverviewFilterBarProps = {
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  nameOptions: {
    label: string;
    value: string;
  }[];
  selectedNames: string[];
  setSelectedNames: React.Dispatch<React.SetStateAction<string[]>>;
  selectedSortMode: CollateralDataQualitySortMode | undefined;
  setSelectedSortMode: React.Dispatch<
    React.SetStateAction<CollateralDataQualitySortMode | undefined>
  >;
  onClearAllFilters: () => void;
};
export const CollateralDataQualityCheckOverviewFilterBar = ({
  searchQuery,
  setSearchQuery,
  nameOptions,
  selectedNames,
  setSelectedNames,
  selectedSortMode,
  setSelectedSortMode,
  onClearAllFilters,
}: CollateralDataQualityCheckOverviewFilterBarProps) => {
  return (
    <Flex
      alignItems="center"
      borderWidth="1px"
      borderColor={colors.gray[200]}
      borderRadius="8px"
      backgroundColor={colors.background[50]}
      w="100%"
      pr="16px"
    >
      <SearchBar
        query={searchQuery}
        onChangeQuery={(newSearchQuery: string) =>
          setSearchQuery(newSearchQuery)
        }
        isDisabled={false}
        backgroundColor={colors.background[50]}
      />
      <IsolatedMultiSelect
        applyButtonText="Apply"
        counterLabel="Names"
        initialSelections={selectedNames.map((option) => option)}
        isSearchable
        onApplyOptions={(selectedOptions) => setSelectedNames(selectedOptions)}
        options={nameOptions}
        optionsHeader="Names"
        placeholderText="Select names"
        showSelectedCount
        titleText="All names"
        buttonProps={{
          borderWidth: '0',
          flex: '0 0 250px',
        }}
      />
      <Select<CollateralDataQualitySortMode>
        value={selectedSortMode}
        onChange={(newValue) => {
          setSelectedSortMode(newValue);
        }}
        options={collateralDataQualitySortModeOptions}
        placeholderLabel="Sort"
        buttonProps={{
          borderWidth: '0',
          width: '250px',
          marginLeft: '16px',
          fontWeight: 500,
        }}
      />
      <Button
        variant="link"
        onClick={onClearAllFilters}
        fontSize="16px"
        fontWeight={500}
        color={colors.info}
        mx="16px"
        textDecoration="none"
        _hover={{ textDecoration: 'underline' }}
      >
        Clear
      </Button>
    </Flex>
  );
};

export const CollateralDataQualityCheckAddMenu = ({
  onClickAddCdqCheck,
}: {
  onClickAddCdqCheck: () => void;
}) => {
  return (
    <Menu>
      <MenuButton as={Button} variant="ghost">
        <Flex alignItems="center" fontWeight={500}>
          <Text mr="8px">Add</Text>
          <Icon icon="chevron-down" />
        </Flex>
      </MenuButton>
      <MenuList>
        <MenuItem onClick={onClickAddCdqCheck} color={colors.black[70]}>
          CDQ Check
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

export const ManualSqlQueryHelperText = () => {
  return (
    <Box
      borderColor={colors.gray[200]}
      borderWidth="1px"
      borderRadius="8px"
      p="24px"
      color={colors.black[70]}
      mb="15px"
    >
      <Text mb="16px">
        To ensure the returning table includes the required columns, follow
        these steps:
      </Text>
      <Text mb="8px">
        <Text as="span" fontWeight={500} color="black">
          1. Specify the Fields:
        </Text>
        &nbsp;Ensure that the
        <Text as="span" color={colors.info} fontWeight={500}>
          &nbsp;SELECT&nbsp;
        </Text>
        statement includes the required fields.
      </Text>
      {cdqDetailTableHeaders.map((requiredHeader) => (
        <Text
          ml="32px"
          color={colors.black[70]}
          key={`helper-${requiredHeader}`}
          mb="8px"
        >
          {`- ${requiredHeader}`}
        </Text>
      ))}
      <Text mb="16px" ml="16px">
        Use
        <Text as="span" color={colors.info} fontWeight={500}>
          &nbsp;AS&nbsp;
        </Text>
        to rename existing fields. Eg.
        <Text as="span" color={colors.info} fontWeight={500}>
          &nbsp;SELECT&nbsp;
        </Text>
        reportingdate
        <Text as="span" color={colors.info} fontWeight={500}>
          &nbsp;AS&nbsp;
        </Text>
        currentReportingDate
      </Text>

      <Text mb="16px">
        <Text as="span" fontWeight={500} color="black">
          2. Identify the Table(s):
        </Text>
        &nbsp;Determine
        <Text as="span" color={colors.info} fontWeight={500}>
          &nbsp;FROM&nbsp;
        </Text>
        which table(s) these columns will be retrieved.
      </Text>
      <Text>
        <Text as="span" fontWeight={500} color="black">
          3. Apply Necessary Conditions:
        </Text>
        &nbsp;Use appropriate
        <Text as="span" color={colors.info} fontWeight={500}>
          &nbsp;WHERE&nbsp;
        </Text>
        clauses to filter the data as needed.
      </Text>
    </Box>
  );
};
